import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { formatTime } from "@clipboard-health/date-time";
import {
  BottomSheet,
  Span,
  Text,
  TextSkeleton,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { ArrivalTimeLabel } from "@src/appV2/redesign/ArrivalTime/TimeLabel";
import { ActivityItem } from "@src/appV2/redesign/ClipboardScore/components/Activity/Item";
import { ActivityItemSkeleton } from "@src/appV2/redesign/ClipboardScore/components/Activity/ItemSkeleton";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { HoldToConfirmButton } from "@src/appV2/redesign/components/HoldToConfirmButton";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { differenceInMinutes } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime } from "date-fns-tz";

export interface ArrivalTimeBottomSheetProps {
  readonly modalState: UseModalState;
  readonly targetDatetime: Date;
  readonly workplaceTimezone: string;
  readonly departureDatetime: Date | undefined;
  readonly gracePeriodInMinutesForDriveETA: number;
  readonly onTimePointsReward?: number;
  readonly latePointsDeduction?: number;
  readonly isLoadingPoints?: boolean;
  readonly isLoadingEta?: boolean;
  readonly isLoadingBooking?: boolean;
  readonly shiftStartDate: Date;
  readonly isWorkerPlacingABid?: boolean;
  readonly onBack?: () => void;
  readonly onConfirm: () => Promise<void>;
}

export function ArrivalTimeBottomSheet(props: ArrivalTimeBottomSheetProps) {
  const {
    modalState,
    targetDatetime,
    workplaceTimezone,
    departureDatetime,
    gracePeriodInMinutesForDriveETA,
    onTimePointsReward,
    latePointsDeduction,
    isLoadingPoints = false,
    isLoadingEta = false,
    isLoadingBooking = false,
    shiftStartDate,
    isWorkerPlacingABid,
    onBack,
    onConfirm,
  } = props;

  const { stage } = useDefinedWorker();
  const isRestricted = stage === WorkerStage.RESTRICTED;

  const minutesTilDeparture = isDefined(departureDatetime)
    ? Math.floor(differenceInMinutes(departureDatetime, new Date()) / 5) * 5 // Floor to nearest 5 minutes
    : undefined;

  function getFormattedDepartureNote() {
    if (!isDefined(departureDatetime) || !isDefined(minutesTilDeparture)) {
      return undefined;
    }

    if (minutesTilDeparture > 60) {
      return `by ${formatTime(utcToZonedTime(departureDatetime, workplaceTimezone))}`;
    }

    return `in the next ${minutesTilDeparture} minutes`;
  }

  const formattedDepartureNote = getFormattedDepartureNote();

  const shouldAlreadyLeave = targetDatetime > shiftStartDate;

  return (
    <BottomSheet
      modalState={modalState}
      header={
        <BottomSheetHeader
          onClose={
            onBack ??
            (() => {
              modalState.closeModal();
            })
          }
        />
      }
      footer={
        <DialogFooter orientation="vertical">
          <HoldToConfirmButton
            fullWidth
            disabled={isLoadingEta || isLoadingPoints}
            isLoading={isLoadingBooking}
            onConfirm={async () => {
              await onConfirm();
            }}
          >
            Press and hold to confirm arrival time
          </HoldToConfirmButton>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack direction="column" sx={{ gap: 5 }}>
          {isLoadingEta ? (
            <Stack direction="column" sx={{ gap: 2, alignItems: "center", paddingTop: 10 }}>
              <TextSkeleton variant="h3" width="60%" />
              <TextSkeleton variant="h3" width="75%" />

              <TextSkeleton variant="body2" width="85%" />
              <TextSkeleton variant="body2" width="85%" />
            </Stack>
          ) : (
            <>
              <Title
                semibold
                component="h3"
                variant="h3"
                sx={{ textAlign: "center", textWrap: "balance" }}
              >
                <Span>
                  {isWorkerPlacingABid
                    ? "If you win the shift, can you arrive before "
                    : "Can you arrive before "}
                </Span>
                <ArrivalTimeLabel datetime={utcToZonedTime(targetDatetime, workplaceTimezone)} />
                <Span>?</Span>
              </Title>

              {isDefined(departureDatetime) && (
                <Text
                  variant="body2"
                  sx={(theme) => ({ textAlign: "center", color: theme.palette.text.secondary })}
                >
                  {shouldAlreadyLeave
                    ? `Based on your current location, you should leave for the shift in the next ${gracePeriodInMinutesForDriveETA} minutes to arrive on time`
                    : `Based on your current location, you should leave for the shift ${formattedDepartureNote} to arrive on time`}
                </Text>
              )}
            </>
          )}

          <Stack sx={{ padding: 7, gap: 5 }}>
            {isLoadingPoints ? (
              <>
                <ActivityItemSkeleton />
                <ActivityItemSkeleton />
              </>
            ) : (
              !isRestricted && (
                <>
                  {Boolean(onTimePointsReward) && (
                    <ActivityItem
                      label="Arrive on time"
                      consequence={onTimePointsReward}
                      consequenceType="bonus"
                    />
                  )}
                  {Boolean(latePointsDeduction) && (
                    <ActivityItem
                      label="Arrive late"
                      consequence={latePointsDeduction}
                      consequenceType="penalty"
                    />
                  )}
                </>
              )
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
